// ./src/components/generic/MediaGalleryField/MediaGalleryField.tsx
//
// A reusable, CONTROLLED photo-gallery field: a flat list of images stored ON the owning
// record (source-agnostic, mirroring the event cover). Every MediaSelector pick — an
// upload, an existing library file, a stock image (Unsplash/…), or a pasted URL —
// becomes a { uploadedFileId?, url } ref that persists on save and survives a reload
// (unlike a context-path gallery, which silently drops library/stock/URL picks). The
// parent owns the value, so this works before the record exists too.
//
// The FIRST image is the "main" one (shown large on the event website; the rest form the
// strip below); the field lets the host reorder which is main via "Make main".
//
// Each ref's `url` is expected to be directly displayable (the caller seeds it from the
// resolved file URL when loading a record); on submit the caller sends uploadedFileId
// for library/upload picks (letting the server resolve the URL) and url for stock/URL.

import React, { useState, useCallback, useRef, useEffect } from "react";
import { Stack, Group, Text, SimpleGrid, Box, ActionIcon, Button, Badge, Tooltip } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppIcon } from "@/components/generic/AppIcon";
import { MediaSelector, MediaSelectorResult } from "@/components/generic/MediaSelector";

export interface MediaImageRef {
  uploadedFileId?: string | null;
  url?: string | null;
}

export interface MediaGalleryFieldProps {
  value: MediaImageRef[];
  onChange: (value: MediaImageRef[]) => void;
  /** Optional context path to organize fresh uploads in the account library. */
  contextPath?: string | null;
  label?: string;
  description?: string;
  addLabel?: string;
  emptyText?: string;
  disabled?: boolean;
}

const toImage = (result: MediaSelectorResult): MediaImageRef | null => {
  if (!result.uploadedFileId && !result.url) {
    return null;
  }

  return { uploadedFileId: result.uploadedFileId ?? null, url: result.url ?? null };
};

const keyOf = (img: MediaImageRef, index: number): string => img.uploadedFileId || img.url || `i${index}`;

const sameRef = (a: MediaImageRef, b: MediaImageRef): boolean =>
  (!!a.uploadedFileId && a.uploadedFileId === b.uploadedFileId) || (!!a.url && a.url === b.url);

export const MediaGalleryField: React.FC<MediaGalleryFieldProps> = ({
  value,
  onChange,
  contextPath,
  label,
  description,
  addLabel,
  emptyText,
  disabled = false,
}) => {
  const { t } = useTranslation(["common"]);

  const [pickerOpen, setPickerOpen] = useState(false);

  // In `multiple` mode the MediaSelector fires onSelect once per pick SYNCHRONOUSLY, so
  // reading the closed-over `value` each time would let each add clobber the previous
  // (only the last would survive). Accumulate through a ref that stays current across
  // those synchronous calls, then emit the fully-built array each time.
  const valueRef = useRef(value);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const handlePick = useCallback(
    (result: MediaSelectorResult) => {
      const img = toImage(result);

      if (!img) {
        return;
      }

      const current = valueRef.current;

      if (current.some((e) => sameRef(e, img))) {
        return;
      }

      const next = [...current, img];
      valueRef.current = next;
      onChange(next);
    },
    [onChange]
  );

  const removeAt = useCallback(
    (index: number) => onChange(value.filter((_, i) => i !== index)),
    [value, onChange]
  );

  const makeMain = useCallback(
    (index: number) => {
      if (index <= 0) {
        return;
      }

      const next = [...value];
      const [moved] = next.splice(index, 1);
      next.unshift(moved);

      onChange(next);
    },
    [value, onChange]
  );

  return (
    <Stack gap="xs">
      <Group justify="space-between" align="center">
        <Stack gap={2}>
          <Text size="md" fw={500}>
            {label ?? t("common:mediaGallery.label", { defaultValue: "Photos" })}
          </Text>
          {description ? (
            <Text size="sm" c="dimmed">
              {description}
            </Text>
          ) : null}
        </Stack>
        <Button
          size="md"
          variant="light"
          leftSection={<AppIcon iconName="Photo" size={16} />}
          onClick={() => setPickerOpen(true)}
          disabled={disabled}
        >
          {addLabel ?? t("common:mediaGallery.add", { defaultValue: "Add photos" })}
        </Button>
      </Group>

      {value.length === 0 ? (
        <Text size="sm" c="dimmed">
          {emptyText ??
            t("common:mediaGallery.empty", {
              defaultValue: "No photos yet. Upload, pick from your library, or choose a stock image.",
            })}
        </Text>
      ) : (
        <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="sm">
          {value.map((img, index) => (
            <Box key={keyOf(img, index)} style={{ position: "relative" }}>
              {img.url ? (
                <img
                  src={img.url}
                  alt=""
                  style={{
                    width: "100%",
                    aspectRatio: "4 / 3",
                    objectFit: "cover",
                    borderRadius: "var(--mantine-radius-sm)",
                    display: "block",
                    border:
                      index === 0 ? "2px solid var(--mantine-color-brand-6)" : "1px solid var(--mantine-color-default-border)",
                  }}
                />
              ) : null}

              {index === 0 ? (
                <Badge
                  size="sm"
                  variant="filled"
                  style={{ position: "absolute", top: 4, left: 4, pointerEvents: "none" }}
                >
                  {t("common:mediaGallery.main", { defaultValue: "Main" })}
                </Badge>
              ) : (
                <Tooltip label={t("common:mediaGallery.makeMain", { defaultValue: "Make main" })} withArrow>
                  <ActionIcon
                    variant="filled"
                    color="gray"
                    size="sm"
                    radius="xl"
                    onClick={() => makeMain(index)}
                    aria-label={t("common:mediaGallery.makeMain", { defaultValue: "Make main" })}
                    disabled={disabled}
                    style={{ position: "absolute", top: 4, left: 4 }}
                  >
                    <AppIcon iconName="Star" size={14} />
                  </ActionIcon>
                </Tooltip>
              )}

              <ActionIcon
                variant="filled"
                color="red"
                size="sm"
                radius="xl"
                onClick={() => removeAt(index)}
                aria-label={t("common:buttons.delete", { defaultValue: "Delete" })}
                disabled={disabled}
                style={{ position: "absolute", top: 4, right: 4 }}
              >
                <AppIcon iconName="X" size={14} />
              </ActionIcon>
            </Box>
          ))}
        </SimpleGrid>
      )}

      <MediaSelector
        opened={pickerOpen}
        onClose={() => setPickerOpen(false)}
        intent="content-image"
        value={null}
        multiple
        onSelect={handlePick}
        contextPath={contextPath ?? null}
      />
    </Stack>
  );
};
